import React, { useState, useEffect, useRef } from 'react';
import './Certificate.css';
import { getFirestore, collection, addDoc, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { Button, Form, Table, Alert, Modal, Tabs, Tab, Row, Col, Card } from 'react-bootstrap';
import { useReactToPrint } from 'react-to-print';

const Certificate = () => {
  const [certificates, setCertificates] = useState([]);
  const [marksheets, setMarksheets] = useState([]);
  const [activeTab, setActiveTab] = useState('certificates');
  const [formData, setFormData] = useState({
    studentName: '',
    courseName: '',
    grade: '',
    issueDate: new Date().toISOString().split('T')[0],
    rollNumber: '',
    duration: '',
    instructor: '',
    organization: 'SchoolERP',
    registrationNumber: '',
    achievements: ''
  });
  const [marksheetData, setMarksheetData] = useState({
    studentName: '',
    fatherName: '',
    motherName: '',
    rollNumber: '',
    enrollmentNumber: '',
    className: '',
    section: '',
    academicYear: new Date().getFullYear(),
    schoolName: 'SchoolERP Academy',
    schoolAddress: '123 Education Street, Learning City, 560001',
    board: 'CBSE',
    subjects: [
      { name: 'Mathematics', theoryMarks: '', practicalMarks: '', totalMarks: '', grade: '' },
      { name: 'Science', theoryMarks: '', practicalMarks: '', totalMarks: '', grade: '' },
      { name: 'English', theoryMarks: '', practicalMarks: '', totalMarks: '', grade: '' },
      { name: 'Social Studies', theoryMarks: '', practicalMarks: '', totalMarks: '', grade: '' },
      { name: 'Computer Science', theoryMarks: '', practicalMarks: '', totalMarks: '', grade: '' },
      { name: 'Hindi', theoryMarks: '', practicalMarks: '', totalMarks: '', grade: '' }
    ],
    totalMarks: '',
    percentage: '',
    finalGrade: '',
    issueDate: new Date().toISOString().split('T')[0],
    principalName: 'Dr. Sharma',
    classTeacher: 'Ms. Gupta'
  });
  const [editingId, setEditingId] = useState(null);
  const [editingMarksheetId, setEditingMarksheetId] = useState(null);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [showMarksheetModal, setShowMarksheetModal] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [selectedMarksheet, setSelectedMarksheet] = useState(null);
  
  const certificateRef = useRef();
  const marksheetRef = useRef();
  const db = getFirestore();

  useEffect(() => {
    fetchCertificates();
    fetchMarksheets();
  }, []);

  const fetchCertificates = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'certificates'));
      const certificateList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCertificates(certificateList);
    } catch (error) {
      showMessage('Error fetching certificates: ' + error.message, 'danger');
    }
  };

  const fetchMarksheets = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'marksheets'));
      const marksheetList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMarksheets(marksheetList);
    } catch (error) {
      showMessage('Error fetching marksheets: ' + error.message, 'danger');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await updateDoc(doc(db, 'certificates', editingId), formData);
        showMessage('Certificate updated successfully!', 'success');
      } else {
        await addDoc(collection(db, 'certificates'), formData);
        showMessage('Certificate created successfully!', 'success');
      }
      resetForm();
      fetchCertificates();
    } catch (error) {
      showMessage('Error: ' + error.message, 'danger');
    }
  };

  const handleMarksheetSubmit = async (e) => {
    e.preventDefault();
    
    // Calculate total marks for each subject and overall
    const updatedSubjects = marksheetData.subjects.map(subject => {
      const theory = parseInt(subject.theoryMarks) || 0;
      const practical = parseInt(subject.practicalMarks) || 0;
      const total = theory + practical;
      
      // Calculate grade for each subject
      let grade = '';
      const percentage = (total / 100) * 100;
      if (percentage >= 90) grade = 'A1';
      else if (percentage >= 80) grade = 'A2';
      else if (percentage >= 70) grade = 'B1';
      else if (percentage >= 60) grade = 'B2';
      else if (percentage >= 50) grade = 'C1';
      else if (percentage >= 40) grade = 'C2';
      else if (percentage >= 33) grade = 'D';
      else grade = 'E';
      
      return {
        ...subject,
        totalMarks: total,
        grade: grade
      };
    });
    
    const totalMarks = updatedSubjects.reduce((sum, subject) => sum + subject.totalMarks, 0);
    const totalPossible = updatedSubjects.length * 100;
    const percentage = ((totalMarks / totalPossible) * 100).toFixed(2);
    
    // Determine final grade
    let finalGrade = '';
    if (percentage >= 90) finalGrade = 'A1';
    else if (percentage >= 80) finalGrade = 'A2';
    else if (percentage >= 70) finalGrade = 'B1';
    else if (percentage >= 60) finalGrade = 'B2';
    else if (percentage >= 50) finalGrade = 'C1';
    else if (percentage >= 40) finalGrade = 'C2';
    else if (percentage >= 33) finalGrade = 'D';
    else finalGrade = 'E';
    
    const finalMarksheetData = {
      ...marksheetData,
      subjects: updatedSubjects,
      totalMarks,
      percentage,
      finalGrade
    };

    try {
      if (editingMarksheetId) {
        await updateDoc(doc(db, 'marksheets', editingMarksheetId), finalMarksheetData);
        showMessage('Marksheet updated successfully!', 'success');
      } else {
        await addDoc(collection(db, 'marksheets'), finalMarksheetData);
        showMessage('Marksheet created successfully!', 'success');
      }
      resetMarksheetForm();
      fetchMarksheets();
    } catch (error) {
      showMessage('Error: ' + error.message, 'danger');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this certificate?')) {
      try {
        await deleteDoc(doc(db, 'certificates', id));
        showMessage('Certificate deleted successfully!', 'success');
        fetchCertificates();
      } catch (error) {
        showMessage('Error deleting certificate: ' + error.message, 'danger');
      }
    }
  };

  const handleMarksheetDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this marksheet?')) {
      try {
        await deleteDoc(doc(db, 'marksheets', id));
        showMessage('Marksheet deleted successfully!', 'success');
        fetchMarksheets();
      } catch (error) {
        showMessage('Error deleting marksheet: ' + error.message, 'danger');
      }
    }
  };

  const handleEdit = (certificate) => {
    setFormData(certificate);
    setEditingId(certificate.id);
  };

  const handleMarksheetEdit = (marksheet) => {
    setMarksheetData(marksheet);
    setEditingMarksheetId(marksheet.id);
  };

  const resetForm = () => {
    setFormData({
      studentName: '',
      courseName: '',
      grade: '',
      issueDate: new Date().toISOString().split('T')[0],
      rollNumber: '',
      duration: '',
      instructor: '',
      organization: 'SchoolERP',
      registrationNumber: '',
      achievements: ''
    });
    setEditingId(null);
  };

  const resetMarksheetForm = () => {
    setMarksheetData({
      studentName: '',
      fatherName: '',
      motherName: '',
      rollNumber: '',
      enrollmentNumber: '',
      className: '',
      section: '',
      academicYear: new Date().getFullYear(),
      schoolName: 'SchoolERP Academy',
      schoolAddress: '123 Education Street, Learning City, 560001',
      board: 'CBSE',
      subjects: [
        { name: 'Mathematics', theoryMarks: '', practicalMarks: '', totalMarks: '', grade: '' },
        { name: 'Science', theoryMarks: '', practicalMarks: '', totalMarks: '', grade: '' },
        { name: 'English', theoryMarks: '', practicalMarks: '', totalMarks: '', grade: '' },
        { name: 'Social Studies', theoryMarks: '', practicalMarks: '', totalMarks: '', grade: '' },
        { name: 'Computer Science', theoryMarks: '', practicalMarks: '', totalMarks: '', grade: '' },
        { name: 'Hindi', theoryMarks: '', practicalMarks: '', totalMarks: '', grade: '' }
      ],
      totalMarks: '',
      percentage: '',
      finalGrade: '',
      issueDate: new Date().toISOString().split('T')[0],
      principalName: 'Dr. Sharma',
      classTeacher: 'Ms. Gupta'
    });
    setEditingMarksheetId(null);
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  const handleCertificatePrint = useReactToPrint({
    content: () => certificateRef.current,
    documentTitle: `Certificate_${selectedCertificate?.studentName}_${selectedCertificate?.courseName}`,
    onAfterPrint: () => console.log('Certificate printed successfully!')
  });

  const handleMarksheetPrint = useReactToPrint({
    content: () => marksheetRef.current,
    documentTitle: `Marksheet_${selectedMarksheet?.studentName}_${selectedMarksheet?.className}`,
    onAfterPrint: () => console.log('Marksheet printed successfully!')
  });

  const handleViewCertificate = (certificate) => {
    setSelectedCertificate(certificate);
    setShowCertificateModal(true);
  };

  const handleViewMarksheet = (marksheet) => {
    setSelectedMarksheet(marksheet);
    setShowMarksheetModal(true);
  };

  const handleCloseModal = () => {
    setShowCertificateModal(false);
    setShowMarksheetModal(false);
    setSelectedCertificate(null);
    setSelectedMarksheet(null);
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleSubjectChange = (index, field, value) => {
    const updatedSubjects = [...marksheetData.subjects];
    updatedSubjects[index] = {
      ...updatedSubjects[index],
      [field]: value
    };
    setMarksheetData({
      ...marksheetData,
      subjects: updatedSubjects
    });
  };

  return (
    <div className="certificate-page">
      <div className="page-header">
        <h2>Certificate & Marksheet Management</h2>
        <p>Create and manage professional A4 size certificates and marksheets</p>
      </div>
      
      {message.text && (
        <Alert variant={message.type} onClose={() => setMessage({ text: '', type: '' })} dismissible>
          {message.text}
        </Alert>
      )}

      <Tabs
        activeKey={activeTab}
        onSelect={(tab) => setActiveTab(tab)}
        className="mb-4 custom-tabs"
      >
        <Tab eventKey="certificates" title="📜 Certificates">
          <Card className="form-card">
            <Card.Header>
              <h4>{editingId ? 'Edit Certificate' : 'Create New Certificate'}</h4>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Student Name *</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.studentName}
                        onChange={(e) => setFormData({...formData, studentName: e.target.value})}
                        required
                        placeholder="Enter student full name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Course Name *</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.courseName}
                        onChange={(e) => setFormData({...formData, courseName: e.target.value})}
                        required
                        placeholder="Enter course name"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Roll Number</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.rollNumber}
                        onChange={(e) => setFormData({...formData, rollNumber: e.target.value})}
                        placeholder="Enter roll number"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Registration Number</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.registrationNumber}
                        onChange={(e) => setFormData({...formData, registrationNumber: e.target.value})}
                        placeholder="Enter registration number"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Grade</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.grade}
                        onChange={(e) => setFormData({...formData, grade: e.target.value})}
                        placeholder="Enter grade (A, B, C, etc.)"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Duration</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.duration}
                        onChange={(e) => setFormData({...formData, duration: e.target.value})}
                        placeholder="e.g., 6 Months, 1 Year"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Instructor Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.instructor}
                        onChange={(e) => setFormData({...formData, instructor: e.target.value})}
                        placeholder="Enter instructor name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Issue Date *</Form.Label>
                      <Form.Control
                        type="date"
                        value={formData.issueDate}
                        onChange={(e) => setFormData({...formData, issueDate: e.target.value})}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Achievements & Special Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.achievements}
                    onChange={(e) => setFormData({...formData, achievements: e.target.value})}
                    placeholder="Enter any special achievements or notes"
                  />
                </Form.Group>

                <div className="form-controls">
                  <Button type="submit" variant="primary" size="lg">
                    {editingId ? 'Update Certificate' : 'Create Certificate'}
                  </Button>
                  {editingId && (
                    <Button variant="secondary" onClick={resetForm}>
                      Cancel Edit
                    </Button>
                  )}
                </div>
              </Form>
            </Card.Body>
          </Card>

          <Card className="mt-4">
            <Card.Header>
              <h4>Existing Certificates</h4>
            </Card.Header>
            <Card.Body>
              <div className="table-container">
                <Table className="certificate-table" responsive>
                  <thead>
                    <tr>
                      <th>Student Name</th>
                      <th>Course</th>
                      <th>Issue Date</th>
                      <th>Grade</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {certificates.map((cert) => (
                      <tr key={cert.id}>
                        <td>{cert.studentName}</td>
                        <td>{cert.courseName}</td>
                        <td>{cert.issueDate}</td>
                        <td><span className="grade-badge">{cert.grade}</span></td>
                        <td>
                          <div className="action-buttons">
                            <Button variant="outline-info" size="sm" className="me-2" onClick={() => handleEdit(cert)}>
                              Edit
                            </Button>
                            <Button variant="outline-danger" size="sm" className="me-2" onClick={() => handleDelete(cert.id)}>
                              Delete
                            </Button>
                            <Button variant="outline-success" size="sm" onClick={() => handleViewCertificate(cert)}>
                              View & Print
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="marksheets" title="📊 Marksheets">
          <Card className="form-card">
            <Card.Header>
              <h4>{editingMarksheetId ? 'Edit Marksheet' : 'Create New Marksheet'}</h4>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleMarksheetSubmit}>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Student Name *</Form.Label>
                      <Form.Control
                        type="text"
                        value={marksheetData.studentName}
                        onChange={(e) => setMarksheetData({...marksheetData, studentName: e.target.value})}
                        required
                        placeholder="Enter student full name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Father's Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={marksheetData.fatherName}
                        onChange={(e) => setMarksheetData({...marksheetData, fatherName: e.target.value})}
                        placeholder="Enter father's name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Mother's Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={marksheetData.motherName}
                        onChange={(e) => setMarksheetData({...marksheetData, motherName: e.target.value})}
                        placeholder="Enter mother's name"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Roll Number *</Form.Label>
                      <Form.Control
                        type="text"
                        value={marksheetData.rollNumber}
                        onChange={(e) => setMarksheetData({...marksheetData, rollNumber: e.target.value})}
                        required
                        placeholder="Enter roll number"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Enrollment Number</Form.Label>
                      <Form.Control
                        type="text"
                        value={marksheetData.enrollmentNumber}
                        onChange={(e) => setMarksheetData({...marksheetData, enrollmentNumber: e.target.value})}
                        placeholder="Enter enrollment number"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Class *</Form.Label>
                      <Form.Control
                        type="text"
                        value={marksheetData.className}
                        onChange={(e) => setMarksheetData({...marksheetData, className: e.target.value})}
                        required
                        placeholder="e.g., 10th, 12th"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Section</Form.Label>
                      <Form.Control
                        type="text"
                        value={marksheetData.section}
                        onChange={(e) => setMarksheetData({...marksheetData, section: e.target.value})}
                        placeholder="e.g., A, B, Science, Commerce"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Academic Year *</Form.Label>
                      <Form.Control
                        type="number"
                        value={marksheetData.academicYear}
                        onChange={(e) => setMarksheetData({...marksheetData, academicYear: e.target.value})}
                        required
                        placeholder="e.g., 2024"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="marksheet-subjects-section">
                  <h5 className="section-title">Subject Marks</h5>
                  <div className="subjects-grid">
                    {marksheetData.subjects.map((subject, index) => (
                      <div key={index} className="subject-card">
                        <h6>{subject.name}</h6>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-2">
                              <Form.Label>Theory Marks</Form.Label>
                              <Form.Control
                                type="number"
                                max="100"
                                value={subject.theoryMarks}
                                onChange={(e) => handleSubjectChange(index, 'theoryMarks', e.target.value)}
                                placeholder="0-100"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-2">
                              <Form.Label>Practical Marks</Form.Label>
                              <Form.Control
                                type="number"
                                max="100"
                                value={subject.practicalMarks}
                                onChange={(e) => handleSubjectChange(index, 'practicalMarks', e.target.value)}
                                placeholder="0-100"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="form-controls">
                  <Button type="submit" variant="primary" size="lg">
                    {editingMarksheetId ? 'Update Marksheet' : 'Create Marksheet'}
                  </Button>
                  {editingMarksheetId && (
                    <Button variant="secondary" onClick={resetMarksheetForm}>
                      Cancel Edit
                    </Button>
                  )}
                </div>
              </Form>
            </Card.Body>
          </Card>

          <Card className="mt-4">
            <Card.Header>
              <h4>Existing Marksheets</h4>
            </Card.Header>
            <Card.Body>
              <div className="table-container">
                <Table className="marksheet-table" responsive>
                  <thead>
                    <tr>
                      <th>Student Name</th>
                      <th>Roll No</th>
                      <th>Class</th>
                      <th>Academic Year</th>
                      <th>Percentage</th>
                      <th>Grade</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {marksheets.map((marksheet) => (
                      <tr key={marksheet.id}>
                        <td>{marksheet.studentName}</td>
                        <td>{marksheet.rollNumber}</td>
                        <td>{marksheet.className}</td>
                        <td>{marksheet.academicYear}</td>
                        <td>
                          <span className={`percentage-badge ${marksheet.percentage >= 60 ? 'high' : marksheet.percentage >= 40 ? 'medium' : 'low'}`}>
                            {marksheet.percentage}%
                          </span>
                        </td>
                        <td><span className="grade-badge">{marksheet.finalGrade}</span></td>
                        <td>
                          <div className="action-buttons">
                            <Button variant="outline-info" size="sm" className="me-2" onClick={() => handleMarksheetEdit(marksheet)}>
                              Edit
                            </Button>
                            <Button variant="outline-danger" size="sm" className="me-2" onClick={() => handleMarksheetDelete(marksheet.id)}>
                              Delete
                            </Button>
                            <Button variant="outline-success" size="sm" onClick={() => handleViewMarksheet(marksheet)}>
                              View & Print
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Certificate Modal */}
      <Modal 
        show={showCertificateModal} 
        onHide={handleCloseModal}
        size="xl"
        centered
        className="certificate-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Certificate Preview - A4 Size</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-0 modal-body-scroll">
          <div ref={certificateRef} className="a4-certificate-container">
            <div className="certificate-a4">
              <div className="certificate-border-a4">
                <div className="certificate-content-a4">
                  {/* Header Section */}
                  <div className="certificate-header-a4">
                    <div className="header-decoration">
                      <div className="decoration-left"></div>
                      <div className="header-title">
                        <h1>CERTIFICATE</h1>
                        <h2>OF ACHIEVEMENT</h2>
                      </div>
                      <div className="decoration-right"></div>
                    </div>
                  </div>
                  
                  {/* Main Content */}
                  <div className="certificate-body-a4">
                    <div className="presented-to-section">
                      <p className="presented-to">PROUDLY PRESENTED TO</p>
                    </div>
                    
                    <div className="student-name-section">
                      <h3 className="student-name">{selectedCertificate?.studentName}</h3>
                    </div>

                    <div className="certificate-text-section">
                      <p className="main-text">
                        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Phasellus sollicitudin enim sapien, 
                        vitae rhoncus massa vehicula in. Nam laoreet augue sem, quis fermentum arcu pellentesque non.
                      </p>
                      <p className="main-text">
                        For outstanding performance and successful completion of the course 
                        <strong> "{selectedCertificate?.courseName}"</strong> with dedication and excellence.
                      </p>
                    </div>

                    <div className="certificate-details-a4">
                      <div className="details-grid">
                        <div className="detail-item">
                          <span>Roll Number:</span>
                          <strong>{selectedCertificate?.rollNumber || 'N/A'}</strong>
                        </div>
                        <div className="detail-item">
                          <span>Duration:</span>
                          <strong>{selectedCertificate?.duration || 'N/A'}</strong>
                        </div>
                        <div className="detail-item">
                          <span>Grade Achieved:</span>
                          <strong>{selectedCertificate?.grade || 'N/A'}</strong>
                        </div>
                        <div className="detail-item">
                          <span>Registration No:</span>
                          <strong>{selectedCertificate?.registrationNumber || 'N/A'}</strong>
                        </div>
                      </div>
                    </div>

                    {selectedCertificate?.achievements && (
                      <div className="achievements-section-a4">
                        <p className="achievements-label">Special Achievements:</p>
                        <p className="achievements-text">"{selectedCertificate.achievements}"</p>
                      </div>
                    )}
                  </div>

                  {/* Footer Section */}
                  <div className="certificate-footer-a4">
                    <div className="signatures-grid">
                      <div className="signature-item">
                        <div className="signature-line"></div>
                        <p className="signature-label">SIGNATURE</p>
                        <p className="signature-name">{selectedCertificate?.instructor || 'Course Instructor'}</p>
                      </div>
                      <div className="signature-item">
                        <div className="signature-line"></div>
                        <p className="signature-label">BEST AWARD</p>
                        <p className="signature-name">SchoolERP Academy</p>
                      </div>
                      <div className="signature-item">
                        <div className="signature-line"></div>
                        <p className="signature-label">SIGNATURE</p>
                        <p className="signature-name">Director</p>
                      </div>
                    </div>
                    <div className="issue-date">
                      <p>Date: {selectedCertificate?.issueDate && formatDate(selectedCertificate.issueDate)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
          <Button variant="primary" onClick={handleCertificatePrint}>
            🖨️ Print Certificate
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Marksheet Modal */}
      <Modal 
        show={showMarksheetModal} 
        onHide={handleCloseModal}
        size="xl"
        centered
        className="marksheet-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Marksheet Preview - A4 Size</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-0 modal-body-scroll">
          <div ref={marksheetRef} className="a4-marksheet-container">
            <div className="marksheet-a4">
              <div className="marksheet-border-a4">
                <div className="marksheet-content-a4">
                  {/* Header */}
                  <div className="marksheet-header-a4">
                    <div className="school-info">
                      <h1>{selectedMarksheet?.schoolName || 'SchoolERP Academy'}</h1>
                      <p className="school-address">{selectedMarksheet?.schoolAddress || '123 Education Street, Learning City, 560001'}</p>
                      <p className="board-name">{selectedMarksheet?.board || 'CBSE'} Board - Academic Transcript</p>
                    </div>
                  </div>

                  {/* Student Information */}
                  <div className="student-info-a4">
                    <div className="info-grid">
                      <div className="info-item">
                        <span>Student Name:</span>
                        <strong>{selectedMarksheet?.studentName}</strong>
                      </div>
                      <div className="info-item">
                        <span>Father's Name:</span>
                        <strong>{selectedMarksheet?.fatherName || 'N/A'}</strong>
                      </div>
                      <div className="info-item">
                        <span>Mother's Name:</span>
                        <strong>{selectedMarksheet?.motherName || 'N/A'}</strong>
                      </div>
                      <div className="info-item">
                        <span>Roll Number:</span>
                        <strong>{selectedMarksheet?.rollNumber}</strong>
                      </div>
                      <div className="info-item">
                        <span>Enrollment No:</span>
                        <strong>{selectedMarksheet?.enrollmentNumber || 'N/A'}</strong>
                      </div>
                      <div className="info-item">
                        <span>Class:</span>
                        <strong>{selectedMarksheet?.className}</strong>
                      </div>
                      <div className="info-item">
                        <span>Section:</span>
                        <strong>{selectedMarksheet?.section || 'N/A'}</strong>
                      </div>
                      <div className="info-item">
                        <span>Academic Year:</span>
                        <strong>{selectedMarksheet?.academicYear}</strong>
                      </div>
                    </div>
                  </div>

                  {/* Marks Table */}
                  <div className="marks-table-a4">
                    <table className="marks-details-a4">
                      <thead>
                        <tr>
                          <th>Subject</th>
                          <th>Theory</th>
                          <th>Practical</th>
                          <th>Total Marks</th>
                          <th>Grade</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedMarksheet?.subjects?.map((subject, index) => (
                          <tr key={index}>
                            <td className="subject-name">{subject.name}</td>
                            <td>{subject.theoryMarks || '0'}/80</td>
                            <td>{subject.practicalMarks || '0'}/20</td>
                            <td><strong>{subject.totalMarks || '0'}/100</strong></td>
                            <td><span className="subject-grade">{subject.grade || 'N/A'}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Performance Summary */}
                  <div className="performance-summary-a4">
                    <div className="summary-grid">
                      <div className="summary-item">
                        <span>Total Marks Obtained:</span>
                        <strong>{selectedMarksheet?.totalMarks || '0'}/600</strong>
                      </div>
                      <div className="summary-item">
                        <span>Percentage:</span>
                        <strong>{selectedMarksheet?.percentage || '0'}%</strong>
                      </div>
                      <div className="summary-item">
                        <span>Final Grade:</span>
                        <strong className="final-grade">{selectedMarksheet?.finalGrade || 'N/A'}</strong>
                      </div>
                      <div className="summary-item">
                        <span>Result:</span>
                        <strong className={`result-status ${selectedMarksheet?.finalGrade === 'E' ? 'fail' : 'pass'}`}>
                          {selectedMarksheet?.finalGrade === 'E' ? 'FAIL' : 'PASS'}
                        </strong>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="marksheet-footer-a4">
                    <div className="signatures-a4">
                      <div className="signature-block">
                        <div className="signature-line"></div>
                        <p>Class Teacher</p>
                        <p className="teacher-name">{selectedMarksheet?.classTeacher || 'Ms. Gupta'}</p>
                      </div>
                      <div className="signature-block">
                        <div className="signature-line"></div>
                        <p>Principal</p>
                        <p className="principal-name">{selectedMarksheet?.principalName || 'Dr. Sharma'}</p>
                      </div>
                      <div className="signature-block">
                        <div className="signature-line"></div>
                        <p>Date</p>
                        <p className="issue-date">{selectedMarksheet?.issueDate && formatDate(selectedMarksheet.issueDate)}</p>
                      </div>
                    </div>
                    <div className="official-seal">
                      <div className="seal">
                        <span>OFFICIAL SEAL</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
          <Button variant="primary" onClick={handleMarksheetPrint}>
            🖨️ Print Marksheet
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Certificate;